"use client";
import styles from "./ProductReviewsSideDrawer.module.scss";
import Rating from "@JohnLewisPartnership/jl-design-system/dist/components/rating/Rating";
import Script from "next/script";
import { Drawer } from "@JohnLewisPartnership/jl-design-system/dist/components/drawer/Drawer";
import { ListItem } from "@/components/__shared__/ListItem/ListItem.tsx";
import { ArrowRightOutlined as ArrowRight } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ArrowRightOutlined";
import { useState } from "react";
import { withSideDrawerAnalytics } from "@/components/ProductSideDrawers/withSideDrawerAnalytics.tsx";
import { Experiences } from "@/getServerSideProps/monetate/getExperiences/experiencesSchema";
import { encodeExperiencesObject } from "@/utils/monetate/encodeExperiencesObject";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace React.JSX {
    export interface IntrinsicElements {
      "jl-reviews": {
        "data-testid"?: string;
        productId: string;
        apiKey: string;
        clientId: string;
        modalRootId: string;
        displayTitle: string;
        accordion: string;
        reviewSummary?: {
          averageRating: number;
          numberOfReviews: number;
        };
        productUrl?: string;
        media?: string;
        experiments?: string;
      };
    }
  }
}

type Props = {
  productId: string;
  reviewsApiKey: string;
  reviewsScriptUrl: string;
  reviewSummary?: { averageRating: number; numberOfReviews: number } | null;
  productUrl?: string;
  experiencesRawObject: Experiences;
};

export function ProductReviewsSideDrawerUI({
  productId,
  reviewsApiKey,
  reviewsScriptUrl,
  reviewSummary,
  productUrl,
  experiencesRawObject,
}: Props) {
  const [open, setOpen] = useState(false);

  let drawerLabel: string | React.JSX.Element = "Reviews";

  if (reviewSummary?.averageRating) {
    const rating = {
      averageRating: reviewSummary.averageRating,
      reviews: reviewSummary.numberOfReviews,
    };
    drawerLabel = (
      <span
        data-testid="product-reviews-label"
        aria-label={`Reviews | ${reviewSummary.averageRating} out of 5 stars`}
      >
        Reviews
        <span
          className={styles.productReviewSummary}
          data-testid="product-review-summary"
        >
          |
          <Rating size="large" suffixType="ratingAndMax" rating={rating} />
        </span>
      </span>
    );
  }

  return (
    <>
      <ListItem
        label={drawerLabel}
        onClick={withSideDrawerAnalytics("Reviews", () => setOpen(true))}
        trailingIcon={<ArrowRight size="medium" />}
        data-testid="drawer:reviews"
      />
      <Drawer
        isOpen={open}
        onClose={() => setOpen(false)}
        heading="Reviews"
        primaryButtonText="Close"
      >
        <div>
          <jl-reviews
            data-testid="product-reviews"
            productId={productId}
            apiKey={reviewsApiKey}
            clientId="web-pdp-scaffold-ui"
            modalRootId="page-root"
            displayTitle="false"
            accordion="false"
            productUrl={productUrl}
            experiments={encodeExperiencesObject(experiencesRawObject)}
          />
        </div>
        <Script
          data-testid="product-reviews-script"
          defer
          type="text/javascript"
          src={reviewsScriptUrl}
        />
      </Drawer>
    </>
  );
}
